import type { FunctionComponent } from "react";
import { useEffect } from "react";

import * as utils from "app/components/filters-bar/utils";
import { useLoading } from "app/custom-hooks/loadingHook";
import { simplePercentageFilter } from "app/filters/ngFilters";
import { BoxLinks } from "app/pages/digital-suite/home-page/components/BoxLinks";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { useWebsitesFavicons } from "app/services/sitesService/hooks/useWebsitesFavicons";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { granularities } from "pro-features/utils";
import { allTrackers } from "app/services/track/track";

const EndPoint = "api/WidgetKpis/TrafficSourcesOverviewAverage/GetPieChartData";

const quickLink = [
    {
        titleKey: "acquisition.home.page.links.organic",
        id: "Organic Search",
        stateLink: "organicsearch_website_organic_search_overview",
    },
    {
        titleKey: "acquisition.home.page.links.paid",
        id: "Paid Search",
        stateLink: "digitalsuite_website_search_overview",
    },
];
const fetchService = DefaultFetchService.getInstance();

export const AcquisitionCustomDropdown: FunctionComponent<{
    domain: string;
    onClearClick: () => void;
}> = ({ domain, onClearClick }) => {
    const [MMXData, MMXDataOps] = useLoading<{ [name: string]: number }>();

    const countries = utils.getCountries(true);
    const { from, to } = DurationService.getDurationData(BasicDurations.LAST_THREE_MONTHS).forAPI;

    const getParams = {
        country: countries[0].id,
        webSource: devicesTypes.DESKTOP,
        keys: domain,
        from: from,
        to: to,
        timeGranularity: granularities[2],
        includeSubDomains: true,
    };
    useEffect(() => {
        MMXDataOps.load(() => {
            return fetchService.get(EndPoint, getParams);
        });
    }, [domain]);

    const { data: domainImageData = {}, isLoading: isDomainImageLoading } = useWebsitesFavicons([domain], undefined, {
        enabled: !!domain,
    });

    const isMMXDataLoading = [useLoading.STATES.INIT, useLoading.STATES.LOADING].includes(MMXData.state);
    const transformData = () => {
        const transformedData = {};
        // if no data set all channels to 0
        if (!MMXData.data.Data[domain]) {
            quickLink.forEach((link) => {
                transformedData[link.id] = 0;
            });
        } else {
            const total = Object.values<number>(MMXData.data.Data[domain]).reduce((sum: number, val) => sum + val, 0);
            Object.keys(MMXData.data.Data[domain]).forEach((channel) => {
                transformedData[channel] = simplePercentageFilter()(MMXData.data.Data[domain][channel] / total, 2);
            });
        }
        return transformedData;
    };

    const onItemClick = (name: string, channelName: string, state: string, visits: string) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `MMX Search Bar/${channelName || ""}/${visits || ""}`);
        const params = {
            country: countries[0].id,
            duration: BasicDurations.LAST_THREE_MONTHS,
            isWWW: "*",
            webSource: devicesTypes.DESKTOP,
            key: name,
        };
        proNavigator.go(state, { ...params });
    };
    return (
        <BoxLinks
            isLoadingData={isMMXDataLoading}
            isLoadingImage={isDomainImageLoading}
            data={!isMMXDataLoading ? transformData() : null}
            name={domain}
            links={quickLink}
            image={!isDomainImageLoading ? domainImageData[domain] : null}
            onClearClick={onClearClick}
            onItemClick={onItemClick}
            countryName={countries[0].text}
        />
    );
};
